import React, { useMemo } from 'react';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { useEsMovil } from '../../../indicadores/hooks/useEsMovil';
import {
  calendarioDeIso,
  esFechaIso,
  etiquetaSemanaDiseno,
  isoDeCalendario,
  lunesDe,
  ms,
  partesSemana,
  sumarDias,
} from '../../utils/semanas';
import { registrarLocaleMetas } from '../../utils/calendarioEs';
import { CLASE_BOTON_ICONO, CLASE_OVERLAY, cx } from './clases';
import { Field } from './Field';

interface Props {
  /** Lunes que se pueden tocar, tal como los manda la API (`semanasDelSelector`). */
  semanas: string[];
  semanaActual: string;
  value: string;
  onChange: (lunes: string) => void;
  /**
   * Sólo el administrador elige cualquier semana (la API se lo permite): sus
   * flechas avanzan de 7 en 7 sin tope y tiene el calendario «Otra semana».
   */
  permitirOtra?: boolean;
  /** Mientras la captura guarda o envía: cambiar de semana dejaba la respuesta sin dueño. */
  disabled?: boolean;
  /** Versión de teléfono («Sem. 40 · 28 sep – 4 oct», 44 px). Por omisión, según el ancho (< 768 px). */
  compacto?: boolean;
  className?: string;
}

/** La semana anterior y la siguiente a la que se puede ir con las flechas. */
export function vecinasDeSemana(
  semanas: string[],
  value: string,
  permitirOtra = false,
): { anterior: string | null; siguiente: string | null } {
  if (!esFechaIso(value)) return { anterior: null, siguiente: null };
  if (permitirOtra) return { anterior: sumarDias(value, -7), siguiente: sumarDias(value, 7) };
  const orden = Array.from(new Set(semanas)).sort((a, b) => ms(a) - ms(b));
  const antes = orden.filter((l) => ms(l) < ms(value));
  const despues = orden.filter((l) => ms(l) > ms(value));
  return { anterior: antes.length ? antes[antes.length - 1] : null, siguiente: despues[0] ?? null };
}

/** Nombre accesible del botón del calendario (PrimeReact lo anunciaba «Choose Date»). */
export const ETIQUETA_OTRA_SEMANA = 'Elegir otra semana';

const LOCALE_CALENDARIO = registrarLocaleMetas();

const Flecha: React.FC<{ dir: 'izq' | 'der' }> = ({ dir }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d={dir === 'izq' ? 'M15 6l-6 6 6 6' : 'M9 6l6 6-6 6'} />
  </svg>
);

/**
 * La semana retail de trabajo: «Semana 40 · 28 sep – 4 oct 2026» entre dos
 * flechas. Las opciones son las VENTANAS que decide el servidor (la semana
 * actual y la siguiente para capturar, y la recién cerrada mientras el regional
 * la resuelve); las flechas no salen de ellas, salvo para el administrador.
 */
export const SelectorSemana: React.FC<Props> = ({
  semanas,
  semanaActual,
  value,
  onChange,
  permitirOtra,
  disabled,
  compacto,
  className,
}) => {
  const esMovil = useEsMovil(767);
  const corto = compacto ?? esMovil;

  const opciones = useMemo(() => {
    const lunes = Array.from(new Set([...semanas, value].filter(Boolean)));
    return lunes
      .sort((a, b) => ms(a) - ms(b))
      .map((l) => ({
        label: `${etiquetaSemanaDiseno(l)}${l === semanaActual ? ' (actual)' : ''}`,
        value: l,
      }));
  }, [semanas, value, semanaActual]);

  const { anterior, siguiente } = vecinasDeSemana(semanas, value, permitirOtra);

  const valor = (o: { value: string } | null) => {
    if (!o || !esFechaIso(o.value)) return <span>Elige la semana</span>;
    const p = partesSemana(o.value);
    const sem = corto ? 'Sem.' : 'Semana';
    const rango = corto ? p.rangoCorto : p.rango;
    return (
      // Si aun así no cabe, «…» y el texto completo en `title`.
      <span className="num block min-w-0 truncate" title={`${sem} ${p.numero} · ${rango}`}>
        <b className="font-semibold">
          {sem} {p.numero}
        </b>{' '}
        · {rango}
      </span>
    );
  };

  const boton = cx(CLASE_BOTON_ICONO, corto && 'h-metas-tactil w-metas-tactil');

  return (
    <div className={cx('flex flex-wrap items-end gap-3', className)} data-testid="selector-semana">
      <Field
        etiqueta="Semana (lunes a domingo)"
        htmlFor="metas-semana"
        // En el teléfono no se ve (diseño `MovilCaptura`): la flecha y el rango ya lo dicen.
        etiquetaOculta={corto}
        className={corto ? 'w-full' : undefined}
      >
        <div className="flex items-center gap-2">
          <button
            type="button"
            className={boton}
            aria-label="Semana anterior"
            disabled={disabled || !anterior}
            onClick={() => anterior && onChange(anterior)}
          >
            <Flecha dir="izq" />
          </button>
          <Dropdown
            inputId="metas-semana"
            value={value}
            options={opciones}
            onChange={(e) => e.value && onChange(e.value)}
            valueTemplate={valor}
            disabled={disabled}
            panelClassName={CLASE_OVERLAY}
            // En el teléfono, sin la flecha de la lista y centrado, como el diseño:
            // con ella «Sem. 40 · 28 sep – 4 oct» no cabía y se cortaba («4 oc»).
            // En escritorio, 350 px y la flecha de 36: a 300 con la de 48, «Semana 44 · 26 oct – 1 nov 2026»
            // salía «… 1 nov 2…» (revisión de la fase B, 21-oct-2026).
            className={cx(corto ? 'metas-semana-corta min-w-0 flex-grow' : 'metas-dropdown-flecha-corta w-[350px]', corto && '!min-h-[44px]')}
          />
          <button
            type="button"
            className={boton}
            aria-label="Semana siguiente"
            disabled={disabled || !siguiente}
            onClick={() => siguiente && onChange(siguiente)}
          >
            <Flecha dir="der" />
          </button>
        </div>
      </Field>
      {permitirOtra && (
        <Field etiqueta="Otra semana" htmlFor="metas-otra-semana">
          <Calendar
            inputId="metas-otra-semana"
            value={calendarioDeIso(value)}
            onChange={(e) => {
              const d = e.value as Date | null | undefined;
              if (d instanceof Date) onChange(lunesDe(isoDeCalendario(d)));
            }}
            dateFormat="dd/mm/yy"
            locale={LOCALE_CALENDARIO}
            showIcon
            disabled={disabled}
            panelClassName={CLASE_OVERLAY}
            // El botón lo nombra el locale («Elegir fecha»); aquí, lo que hace.
            pt={{ dropdownButton: { root: { 'aria-label': ETIQUETA_OTRA_SEMANA } } }}
          />
        </Field>
      )}
    </div>
  );
};

export default SelectorSemana;
